import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import Joyride from 'react-joyride';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import WelcomeDialog from '../components/WelcomeDialog/WelcomeDialog';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useOnboarding } from '../hooks/useOnboarding';
import AiSelectorBar from '../components/AiSelectorBar/index';
import AiInfoBar from '../components/AiInfoBar/index';
import AiChartSection from '../components/AiChartSection/index';
import AiPositionsTable from '../components/AiPositionsTable/index';
import AiLogPanel from '../components/AiLogPanel/index';
import { useAiTrader } from '../hooks/useAiTrader';
import { getAiTraderTourSteps } from '../config/aiTraderTourSteps';
import { API_BASE_URL } from '../utils/apiConfig';
import './AiTraderPage.css';

const WELCOME_MESSAGE = (
  <>
    <p>We spent months training an AI alongside expert analysts and trading bibles. We handed it $10,000 with one mission: get rich.</p>
    <p>Now, it is completely on its own. We will not interfere, even if it fails. You will witness every move it makes, in real time, right before your eyes.</p>
    <p>AI Trader Agent doesn't give signals or investment advice. It is not just a bot. It thinks like a human, feels the pressure, and learns from its mistakes.</p>
    <p className="ai-welcome-tagline">IN CODE WE TRUST.</p>
  </>
);

function AiTraderPage() {
  const showAiTrader = useFeatureFlag('show-ai-trader-nav', true);
  const { challenge, positions, decisions, closedTrades, connectionStatus } = useAiTrader();

  const tourSteps = useMemo(() => getAiTraderTourSteps(), []);
  const {
    tourRunning,
    stepIndex,
    showWelcome,
    startTour,
    dismissWelcome,
    restartTour,
    handleJoyrideCallback
  } = useOnboarding('ai-trader', { totalSteps: tourSteps.length });

  const [trades, setTrades] = useState([]);
  const [equityCurve, setEquityCurve] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const isDev = window.location.port === '5173' || window.location.port === '5174' || window.location.port === '5175';
    const baseUrl = isDev ? 'http://localhost:3000' : API_BASE_URL;

    const fetchData = async () => {
      try {
        const [tradesRes, equityRes, statsRes] = await Promise.allSettled([
          fetch(`${baseUrl}/api/ai-trader/trades?limit=50`),
          fetch(`${baseUrl}/api/ai-trader/equity-curve`),
          fetch(`${baseUrl}/api/ai-trader/stats`),
        ]);

        if (tradesRes.status === 'fulfilled' && tradesRes.value.ok) {
          const data = await tradesRes.value.json();
          setTrades(data.data || []);
        }
        if (equityRes.status === 'fulfilled' && equityRes.value.ok) {
          const data = await equityRes.value.json();
          setEquityCurve(data.data || []);
        }
        if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
          const data = await statsRes.value.json();
          setStats(data);
        }
      } catch (err) {
        console.error('AI Trader: failed to fetch initial data', err);
      }
    };

    fetchData();
  }, []);

  if (!showAiTrader) {
    return <Navigate to="/trades" replace />;
  }

  return (
    <div className="ai-trader-page">
      <SEO page="aiTrader" />
      <Header
        isConnected={connectionStatus === 'connected'}
        isSubscribed={connectionStatus === 'connected'}
        onStartTour={restartTour}
      />

      {/* AI Model Selector Bar */}
      <AiSelectorBar />

      {/* Main Container: Left (chart) + Right (sidebar) */}
      <div className="ai-trader-main">
        {/* Left Panel */}
        <div className="ai-trader-left">
          <AiInfoBar challenge={challenge} />
          <AiChartSection
            equityCurve={equityCurve}
            challenge={challenge}
          />
        </div>

        {/* Right Sidebar */}
        <div className="ai-trader-right">
          <AiPositionsTable
            positions={positions}
            closedTrades={closedTrades}
            trades={trades}
            decisions={decisions}
          />
          <AiLogPanel
            decisions={decisions}
            connectionStatus={connectionStatus}
          />
        </div>
      </div>

      {/* Onboarding Tour */}
      <WelcomeDialog
        show={showWelcome}
        onStartTour={startTour}
        onDismiss={dismissWelcome}
        title="Welcome to Crypto's Craziest Experiment!"
        message={WELCOME_MESSAGE}
        primaryLabel="Show me around"
        secondaryLabel="Let me watch"
      />
      <Joyride
        steps={tourSteps}
        run={tourRunning}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous
        showSkipButton
        showProgress
        disableScrolling
        disableOverlayClose
        spotlightPadding={8}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Got it!',
          next: 'Next',
          skip: 'Skip tour'
        }}
        styles={{
          options: {
            zIndex: 10000,
            arrowColor: '#1a1a1a',
            backgroundColor: '#1a1a1a',
            overlayColor: 'rgba(0, 0, 0, 0.6)',
            primaryColor: '#fff',
            textColor: '#fff'
          }
        }}
      />
    </div>
  );
}

export default AiTraderPage;
